import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CaretLeft, Clock, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { BLOG_POSTS } from '../data/blogPosts';

const TAG_COLORS = {
  'AI':               'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  'Investing Basics': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  'Psychology':       'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30',
  'Markets':          'bg-amber-500/10 text-amber-300 border-amber-500/30',
};

const Blog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTag, setActiveTag] = useState('All');
  const [openPost, setOpenPost] = useState(null);

  const tags = ['All', 'AI', 'Investing Basics', 'Psychology', 'Markets'];
  const filtered = activeTag === 'All'
    ? BLOG_POSTS
    : BLOG_POSTS.filter((p) => p.tag === activeTag);

  return (
    <div className="min-h-screen noise-bg" data-testid="blog-page">
      {user ? <Navbar /> : (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-400 hover:text-gold transition-colors"
            data-testid="blog-back-btn"
          >
            <CaretLeft size={16} weight="bold" /> Back
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-3">The TeenVest Journal</p>
          <h1 className="text-4xl md:text-6xl font-unbounded font-bold text-white mb-3">
            Ideas that make investors.
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl">
            Sharp reads on markets, AI, psychology and the basics — written for the next generation.
          </p>
        </motion.div>

        {/* Tag filter pills */}
        <div className="flex flex-wrap gap-2 mb-10">
          {tags.map((t) => (
            <button
              key={t}
              onClick={() => setActiveTag(t)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeTag === t
                  ? 'bg-gold text-black border-gold'
                  : 'bg-white/5 text-zinc-300 border-white/10 hover:border-white/30'
              }`}
              data-testid={`blog-tag-${t.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Grid of cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((post, i) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              onClick={() => setOpenPost(post)}
              className="group cursor-pointer glass-card p-6 flex flex-col hover:border-gold/40 transition-colors"
              data-testid={`blog-card-${post.slug}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${TAG_COLORS[post.tag] || 'bg-white/5 text-zinc-300 border-white/10'}`}>
                  {post.tag}
                </span>
                <span className="flex items-center gap-1 text-xs text-zinc-500">
                  <Clock size={12} /> {post.readingTime} min
                </span>
              </div>
              <h3 className="text-xl font-unbounded font-bold text-white mb-2 group-hover:text-gold transition-colors">
                {post.title}
              </h3>
              <p className="text-sm text-zinc-400 mb-4 flex-1 leading-relaxed">
                {post.preview}
              </p>
              <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/5">
                <span className="text-xs text-zinc-500">{post.author} · {post.date}</span>
                <span className="text-gold flex items-center gap-1 text-sm group-hover:translate-x-1 transition-transform">
                  Read <ArrowRight size={14} weight="bold" />
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-zinc-500 py-16">No posts under this tag yet.</p>
        )}
      </div>

      {/* Article reader modal */}
      {openPost && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto"
          onClick={() => setOpenPost(null)}
          data-testid="blog-article-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-3xl w-full bg-zinc-950 border border-white/10 rounded-2xl p-6 md:p-10 my-6"
          >
            <button
              onClick={() => setOpenPost(null)}
              className="text-zinc-400 hover:text-gold text-sm mb-4"
              data-testid="blog-article-close"
            >
              &larr; Back to Journal
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${TAG_COLORS[openPost.tag] || ''}`}>
                {openPost.tag}
              </span>
              <span className="text-xs text-zinc-500">{openPost.readingTime} min read</span>
            </div>

            <h1 className="text-3xl md:text-4xl font-unbounded font-bold text-white mb-3">{openPost.title}</h1>
            <p className="text-sm text-zinc-500 mb-8">By {openPost.author} · {openPost.date}</p>

            <article className="prose prose-invert max-w-none space-y-4 text-zinc-200 leading-relaxed">
              {openPost.body.map((block, idx) => {
                if (block.type === 'h2') return <h2 key={idx} className="text-xl md:text-2xl font-unbounded font-bold text-gold mt-6 mb-2">{block.text}</h2>;
                return <p key={idx}>{block.text}</p>;
              })}
            </article>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Blog;
